/**
 * "Weiter als …"-Login-Hinweis (Issue #634).
 *
 * Reine Helfer ohne Angular/Firebase-Abhängigkeiten, damit sie per Vitest
 * (ohne TestBed) testbar sind. Der Service kapselt nur den localStorage-Zugriff.
 */

export type LastLoginProvider =
  | 'google'
  | 'apple'
  | 'microsoft'
  | 'email'
  | 'phone';

export interface LastLoginInfo {
  provider: LastLoginProvider;
  /** Anzeigename, fällt auf E-Mail/Telefonnummer zurück. */
  displayName: string;
  /** E-Mail oder (bei Phone-only-Usern) die Telefonnummer als Identifier. */
  email: string;
  /** Profilbild, falls der Provider eines liefert (i. d. R. nur Google). */
  photoURL?: string;
}

/** localStorage-Schlüssel für den zuletzt genutzten Login. */
export const LAST_LOGIN_KEY = 'awoCareLastLogin';

/** Firebase `providerData[0].providerId` → interner Provider-Schlüssel. */
export function mapProviderId(
  providerId: string | null | undefined,
): LastLoginProvider {
  switch (providerId) {
    case 'google.com':
      return 'google';
    case 'apple.com':
      return 'apple';
    case 'microsoft.com':
      return 'microsoft';
    case 'phone':
      return 'phone';
    case 'password':
      return 'email';
    default:
      // Unbekannte/leere Provider als E-Mail behandeln (sicherster Fallback).
      return 'email';
  }
}

/** Minimaler User-Ausschnitt (strukturell kompatibel mit Firebase `User`). */
export interface LastLoginUserLike {
  providerData?: ReadonlyArray<{ providerId?: string | null } | null>;
  displayName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  photoURL?: string | null;
}

/** Baut das zu speichernde `LastLoginInfo` aus einem Firebase-User. */
export function buildLastLoginInfo(user: LastLoginUserLike): LastLoginInfo {
  const providerId = user.providerData?.[0]?.providerId ?? '';
  const provider = mapProviderId(providerId);
  // Phone-only-User: Telefonnummer liegt als Identifier vor (siehe CLAUDE.md).
  const identifier = user.email || user.phoneNumber || '';
  return {
    provider,
    displayName: user.displayName || identifier,
    email: identifier,
    photoURL: user.photoURL || undefined,
  };
}
